import React, { useState } from "react";
import * as XLSX from 'xlsx';
import {
  Flex,
  Typography,
  Table,
  Space,
  Button,
  Radio,
  Tooltip
} from "antd";
import { 
  SortAscendingOutlined, 
  SortDescendingOutlined, 
  StockOutlined, 
  UnorderedListOutlined,
  FontSizeOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DownloadOutlined
} from "@ant-design/icons";
import { useGetUsimStockQuery } from "../../../../api/usim/inventory/usimInventoryApi";

const { Title } = Typography;

interface TableRecord {
  key: string;
  type: string;
  stock: number;
  isPlan?: boolean;
  children?: TableRecord[];
}

export default function UsimInventoryReportPage() {
  const [sortQty, setSortQty] = useState<'high' | 'low' | undefined>(undefined);
  const [sortType, setSortType] = useState<'asc' | 'desc' | undefined>(undefined);
  const [sortPlan, setSortPlan] = useState<'asc' | 'desc' | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading, isFetching } = useGetUsimStockQuery({
    sort_qty: sortQty,
    sort_type: sortType,
    sort_plan: sortPlan
  });

  const handleExport = () => {
    if (!data?.stock) return;
    setIsExporting(true);
    
    // Use setTimeout to allow UI to render the loading state before heavy processing
    setTimeout(() => {
      try {
        const rows: any[] = [];
        Object.entries(data.stock).forEach(([typeName, typeData]) => {
          Object.entries(typeData.items).forEach(([planName, qty]) => {
            rows.push({
              "Product Type": typeName,
              "Plan Name": planName,
              "Stock Quantity": qty
            });
          });
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory Stock");
        XLSX.writeFile(workbook, `Inventory_Stock_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      } catch (error) {
        console.error("Export failed:", error);
      } finally {
        setIsExporting(false);
      }
    }, 100);
  };

  const handleSortQty = () => {
    setSortQty(current => current === 'high' ? 'low' : 'high');
    setSortType(undefined); // Clear type sort
  };

  const handleSortType = () => {
    setSortType(current => current === 'asc' ? 'desc' : 'asc');
    setSortQty(undefined); // Clear qty sort
  };

  const handleSortPlan = () => {
    setSortPlan(current => current === 'asc' ? 'desc' : 'asc');
  };

  const dataSource: TableRecord[] = React.useMemo(() => {
    if (!data?.stock) return [];

    return Object.entries(data.stock).map(([typeName, typeData]) => {
      const children: TableRecord[] = Object.entries(typeData.items).map(([planName, qty]) => ({
        key: `${typeName}-${planName}`,
        type: planName, // Showing plan name in the same column
        stock: qty,
        isPlan: true
      }));

      return {
        key: typeName,
        type: typeName,
        stock: typeData.totalStock,
        children: children.length > 0 ? children : undefined
      };
    });
  }, [data]);

  const columns = [
    {
      title: 'Product Type / Plan Name',
      dataIndex: 'type',
      key: 'type',
      render: (text: string, record: TableRecord) => (
        <span style={{ fontWeight: record.isPlan ? 'normal' : 'bold', paddingLeft: record.isPlan ? 20 : 0 }}>
          {text}
        </span>
      )
    },
    {
      title: 'Stock Quantity',
      dataIndex: 'stock',
      key: 'stock',
    }
  ];

  return (
    <Flex gap={16} vertical style={{ padding: "24px" }}>
      <Flex justify="space-between" align="center">
        <Title level={2}>Inventory Report</Title>
        <Space>
          <Tooltip title={`Sort by Stock Quantity (${sortQty === 'high' ? 'High to Low' : 'Low to High'})`}>
            <Button 
              onClick={handleSortQty} 
              type={sortQty ? 'primary' : 'default'}
              icon={<StockOutlined />}
            >
              Quantity {sortQty === 'high' ? <ArrowDownOutlined /> : sortQty === 'low' ? <ArrowUpOutlined /> : ''}
            </Button>
          </Tooltip>

          <Tooltip title={`Sort by Type Name (${sortType === 'asc' ? 'A-Z' : 'Z-A'})`}>
            <Button 
              onClick={handleSortType} 
              type={sortType ? 'primary' : 'default'}
              icon={<FontSizeOutlined />}
            >
              Type {sortType === 'asc' ? <ArrowDownOutlined /> : sortType === 'desc' ? <ArrowUpOutlined /> : ''}
            </Button>
          </Tooltip>

          <Tooltip title={`Sort by Plan Name (${sortPlan === 'asc' ? 'A-Z' : 'Z-A'})`}>
            <Button 
              onClick={handleSortPlan} 
              type={sortPlan ? 'primary' : 'default'}
              icon={<UnorderedListOutlined />}
            >
              Plan {sortPlan === 'asc' ? <ArrowDownOutlined /> : sortPlan === 'desc' ? <ArrowUpOutlined /> : ''}
            </Button>
          </Tooltip>

          <Button 
            onClick={handleExport} 
            loading={isExporting}
            icon={<DownloadOutlined />}
            disabled={!data?.stock}
          >
            Export to Excel
          </Button>
        </Space>
      </Flex>

      <Table
        loading={isLoading || isFetching}
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        expandable={{
          defaultExpandAllRows: false
        }}
        rowClassName={(record) => record.isPlan ? 'plan-row' : 'type-row'}
      />
    </Flex>
  );
}
